import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { useNotificationModal } from '@/contexts/NotificationModalContext';
import { 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  FileText, 
  User,
  Calendar,
  DollarSign,
  Check,
  GripVertical,
  MoreHorizontal,
  Copy,
  ExternalLink,
  MessageCircle,
  Mail
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Feedback {
  id: string;
  tipo: string;
  descricao: string;
  createdAt: string;
  acaoGestor: boolean;
  acaoGestorConcluida: boolean;
  acaoGestorNegada: boolean;
  pagamentoConfirmado?: boolean;
  observacoesGestor?: string | null;
  dataAcaoGestor?: string | null;
  motivoNegacao?: string | null;
  tipoNegociacao?: string | null;
  valorNegociado?: string | null;
  proximoContato?: string | null;
  audio?: {
    id: string;
    fileName: string;
    originalName: string;
    fileSize: number;
    mimeType: string;
    url: string;
  } | null;
  user: {
    id: string;
    name: string;
    email: string;
  };
  titulo?: {
    id: string;
    numero: string;
    valor: number;
    status: string;
    company: {
      id: string;
      name: string;
    };
    clienteFornecedor: {
      id: string;
      nome: string;
      codigo: string;
      telefone?: string;
      email?: string;
    };
  };
  clienteFornecedor?: {
    id: string;
    nome: string;
    codigo: string;
    telefone?: string;
    email?: string;
    company: {
      id: string;
      name: string;
    };
  };
  historicoPagamentos: Array<{
    id: string;
    valorNegociado: number;
    statusPagamento: string;
    titulo: {
      id: string;
      numero: string;
      valor: number;
    };
  }>;
  titulosNegociados?: Array<{
    id: string;
    numero: string;
    valor: number;
    vencimento: string;
    status: string;
    descricaoTitulo?: string;
    observacoes?: string;
    company: {
      id: string;
      name: string;
    };
  }>;
}


// Componente SortableCard para o Kanban
const SortableFeedbackCard = ({ 
  feedback, 
  onOpenDetails,
}: { 
  feedback: Feedback; 
  onOpenDetails: (feedback: Feedback) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: feedback.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getStatusBadge = (feedback: Feedback) => {
    if (feedback.acaoGestorConcluida) {
      return (
        <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
          <CheckCircle className="w-3 h-3 mr-1" />
          Concluída
        </Badge>
      );
    }
    if (feedback.acaoGestorNegada) {
      return (
        <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
          <AlertCircle className="w-3 h-3 mr-1" />
          Negada
        </Badge>
      );
    }
    return (
      <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
        <Clock className="w-3 h-3 mr-1" />
        Pendente
      </Badge>
    );
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-3 cursor-pointer hover:shadow-md transition-shadow ${
        isDragging ? 'opacity-50' : ''
      }`}
      onClick={() => onOpenDetails(feedback)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <span
                {...attributes}
                {...listeners}
                className="inline-flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-slate-300 cursor-grab active:cursor-grabbing"
                aria-label="Arrastar"
              >
                <GripVertical className="h-4 w-4" />
              </span>
              {getStatusBadge(feedback)}
            </div>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                console.log('Botão de detalhes clicado para feedback:', feedback.id);
                onOpenDetails(feedback);
              }}
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600 dark:text-slate-300"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </div>
          
          <h4 className="font-medium text-gray-900 dark:text-white mb-1">
            {feedback.titulo?.clienteFornecedor?.nome || feedback.clienteFornecedor?.nome}
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Empresa: {feedback.titulo?.company?.name || feedback.clienteFornecedor?.company?.name || '—'}
          </p>
          
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            {feedback.descricao.substring(0, 100)}...
          </p>

          <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400 mb-3">
            <div className="flex items-center space-x-1">
              <User className="h-3 w-3" />
              <span>{feedback.user.name}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Calendar className="h-3 w-3" />
              <span>{formatDate(feedback.createdAt)}</span>
            </div>
          </div>

          {feedback.titulosNegociados && feedback.titulosNegociados.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Títulos Relacionados:
              </p>
              <div className="space-y-1">
                {feedback.titulosNegociados.map((titulo, index) => (
                  <div key={index} className="text-xs bg-gray-50 dark:bg-gray-700 p-2 rounded">
                    <div className="flex justify-between">
                      <span className="font-medium">{titulo.numero}</span>
                      <span className="text-green-600 dark:text-green-400">
                        {formatCurrency(titulo.valor)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {feedback.observacoesGestor && (
            <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
              <p className="text-xs text-blue-700 dark:text-blue-300 font-medium mb-1">
                Observações do Gestor:
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                {feedback.observacoesGestor}
              </p>
            </div>
          )}

          {feedback.acaoGestorNegada && feedback.motivoNegacao && (
            <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
              <p className="text-xs text-red-700 dark:text-red-300 font-medium mb-1">
                Motivo da Negação:
              </p>
              <p className="text-xs text-red-600 dark:text-red-400">
                {feedback.motivoNegacao}
              </p>
            </div>
          )}
        </div>
      </div>


    </div>
  );
};

export const GestorCobrancasPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { isGestorModalOpen, closeGestorModal } = useNotificationModal();

  // Função para copiar texto
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link copiado para a área de transferência!');
    } catch (error) {
      console.error('Erro ao copiar:', error);
      toast.error('Erro ao copiar link');
    }
  };

  // Função para abrir WhatsApp
  const openWhatsApp = (url: string, clienteNome: string, telefone?: string) => {
    const message = `Olá ${clienteNome}! Segue o link do boleto para pagamento: ${url}`;
    let whatsappUrl = '';
    
    if (telefone) {
      // Remove caracteres não numéricos do telefone
      const cleanPhone = telefone.replace(/\D/g, '');
      whatsappUrl = `https://wa.me/55${cleanPhone}?text=${encodeURIComponent(message)}`;
    } else {
      whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    }
    
    window.open(whatsappUrl, '_blank');
  };

  // Função para abrir email
  const openEmail = (url: string, clienteNome: string, email?: string) => {
    const subject = `Boleto para pagamento - ${clienteNome}`;
    const body = `Olá!\n\nSegue o link do boleto para pagamento:\n\n${url}\n\nAtenciosamente,\nEquipe de Cobranças`;
    
    let emailUrl = `mailto:${email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    window.open(emailUrl, '_blank');
  };

  // Abrir modal quando notificação for clicada
  useEffect(() => {
    if (isGestorModalOpen) {
      setIsModalOpen(true);
      closeGestorModal(); // Fechar o contexto após abrir o modal
    }
  }, [isGestorModalOpen, closeGestorModal]);
  // Empresas e operadores
  const { data: companies } = useQuery({
    queryKey: ['companies', 'active'],
    queryFn: async () => {
      const response = await api.get('/companies?isActive=true');
      const payload = response.data?.data;
      return Array.isArray(payload) ? payload : (payload?.companies ?? []);
    },
  });

  const { data: operators } = useQuery({
    queryKey: ['operators', 'all'],
    queryFn: async () => {
      const response = await api.get('/email-automation/operators');
      return response.data?.data ?? response.data ?? [];
    },
  });
  const [filters, setFilters] = useState({
    acaoGestorConcluida: '',
    operadorId: '',
    companyId: '',
    startDate: '',
    endDate: '',
  });
  const [sortBy, setSortBy] = useState<'date' | 'value'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [concluirForm, setConcluirForm] = useState({
    observacoesGestor: '',
    alteracoesRealizadas: [] as string[],
  });
  const [isNegarModalOpen, setIsNegarModalOpen] = useState(false);
  const [negarForm, setNegarForm] = useState({
    motivoNegacao: '',
  });

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  
  // Debug logs
  console.log('GestorCobrancasPage render - isDetailsOpen:', isDetailsOpen, 'selectedFeedback:', selectedFeedback?.id);

  // Sensores para drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Buscar feedbacks
  const { data: feedbacksData, isLoading } = useQuery({
    queryKey: ['gestor-cobrancas-feedbacks', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await api.get(`/gestor-cobrancas/feedbacks?${params.toString()}`);
      return response.data.data;
    },
    refetchInterval: 30000, // Atualizar a cada 30 segundos
    refetchIntervalInBackground: true, // Continuar atualizando mesmo com aba inativa
  });




  // Mutação para concluir ação
  const concluirAcaoMutation = useMutation({
    mutationFn: async (data: { feedbackId: string; observacoesGestor: string; alteracoesRealizadas: string[] }) => {
      const response = await api.post('/gestor-cobrancas/concluir-acao', data);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Ação concluída com sucesso!');
      setIsModalOpen(false);
      setSelectedFeedback(null);
      setConcluirForm({ observacoesGestor: '', alteracoesRealizadas: [] });
      queryClient.invalidateQueries({ queryKey: ['gestor-cobrancas-feedbacks'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao concluir ação');
    },
  });

  // Mutação para negar solicitação
  const negarSolicitacaoMutation = useMutation({
    mutationFn: async (data: { feedbackId: string; motivoNegacao: string }) => {
      const response = await api.post('/gestor-cobrancas/negar-solicitacao', data);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Solicitação negada com sucesso!');
      setIsNegarModalOpen(false);
      setSelectedFeedback(null);
      setNegarForm({ motivoNegacao: '' });
      queryClient.invalidateQueries({ queryKey: ['gestor-cobrancas-feedbacks'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao negar solicitação');
    },
  });

  // Mutação para marcar pagamento como confirmado
  const confirmarPagamentoMutation = useMutation({
    mutationFn: async ({ feedbackId }: { feedbackId: string }) => {
      const response = await api.patch(`/gestor-cobrancas/confirmar-pagamento/${feedbackId}`);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Pagamento confirmado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['gestor-cobrancas-feedbacks'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao confirmar pagamento');
    },
  });

  const handleConcluirAcao = () => {
    if (!selectedFeedback || !concluirForm.observacoesGestor.trim()) {
      toast.error('Observações são obrigatórias');
      return;
    }

    concluirAcaoMutation.mutate({
      feedbackId: selectedFeedback.id,
      observacoesGestor: concluirForm.observacoesGestor,
      alteracoesRealizadas: concluirForm.alteracoesRealizadas,
    });
  };

  const openConcluirModal = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setIsModalOpen(true);
  };

  const openNegarModal = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setIsNegarModalOpen(true);
  };

  const handleNegarSolicitacao = () => {
    if (!selectedFeedback || !negarForm.motivoNegacao.trim()) {
      toast.error('Motivo da negação é obrigatório');
      return;
    }

    negarSolicitacaoMutation.mutate({
      feedbackId: selectedFeedback.id,
      motivoNegacao: negarForm.motivoNegacao,
    });
  };



  // Função para confirmar pagamento
  const confirmarPagamento = (feedbackId: string) => {
    confirmarPagamentoMutation.mutate({ feedbackId });
  };

  // Função para filtrar feedbacks baseada na data e status
  const getFilteredFeedbacks = (feedbacks: Feedback[]) => {
    if (!feedbacks) return [];
    
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    
    return feedbacks.filter((feedback) => {
      const feedbackDate = new Date(feedback.createdAt);
      feedbackDate.setHours(0, 0, 0, 0);
      
      // Se há filtro de data, usar a data selecionada
      if (filters.startDate || filters.endDate) {
        const selectedDate = filters.startDate ? new Date(filters.startDate) : new Date(filters.endDate);
        selectedDate.setHours(0, 0, 0, 0);
        
        // Para feedbacks pendentes: sempre mostrar independente da data
        if (!feedback.acaoGestorConcluida && !feedback.acaoGestorNegada) {
          return true;
        }
        
        // Para feedbacks concluídos/negados/pagamento confirmado: mostrar apenas na data selecionada
        return feedbackDate.getTime() === selectedDate.getTime();
      }
      
      // Se não há filtro de data: mostrar todos os feedbacks
      return true;
    });
  };

  // Função para ordenar feedbacks
  const getSortedFeedbacks = (feedbacks: Feedback[]) => {
    if (!feedbacks) return [];
    
    return [...feedbacks].sort((a, b) => {
      if (sortBy === 'date') {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      } else if (sortBy === 'value') {
        // Calcular valor total do feedback
        const getFeedbackValue = (feedback: Feedback) => {
          if (feedback.titulosNegociados && feedback.titulosNegociados.length > 0) {
            return feedback.titulosNegociados.reduce((sum, titulo) => sum + Number(titulo.valor), 0);
          } else if (feedback.titulo) {
            return Number(feedback.titulo.valor);
          }
          return 0;
        };
        
        const valueA = getFeedbackValue(a);
        const valueB = getFeedbackValue(b);
        return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
      }
      return 0;
    });
  };

  return (
    <>
      <Helmet>
        <title>Gestor de Cobranças - ZehMotoca</title>
      </Helmet>

      <div className="h-full flex flex-col w-full" data-testid="page-gestor-cobrancas">
        {/* Header fixo */}
        <div className="flex-shrink-0 p-4 pb-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 w-full">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Gestor de Cobranças
              </h1>
            </div>
            <div className="flex items-center space-x-3">
              {/* Indicador de Atualização Automática */}
              <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-slate-400">
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>Auto-atualização a cada 30s</span>
                </div>
              </div>

              {/* Filtros */}
              <div className="flex items-center space-x-2">
                <Select
                  value={filters.acaoGestorConcluida}
                  onValueChange={(value) => setFilters({ ...filters, acaoGestorConcluida: value })}
                >
                  <option value="">Todos os status</option>
                  <option value="false">Pendentes</option>
                  <option value="true">Concluídas</option>
                  <option value="negadas">Negadas</option>
                </Select>

                <Select
                  value={filters.companyId}
                  onValueChange={(value) => setFilters({ ...filters, companyId: value })}
                >
                  <option value="">Todas as empresas</option>
                  {(Array.isArray(companies) ? companies : []).map((company: any) => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </Select>

                <Select
                  value={filters.operadorId}
                  onValueChange={(value) => setFilters({ ...filters, operadorId: value })}
                >
                  <option value="">Todos os operadores</option>
                  {(Array.isArray(operators) ? operators : []).map((op: any) => (
                    <option key={op.id} value={op.id}>{op.name}</option>
                  ))}
                </Select>

                <Input
                  placeholder="Data início"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                  className="w-32"
                />

                <Input
                  placeholder="Data fim"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                  className="w-32"
                />

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilters({ acaoGestorConcluida: '', operadorId: '', companyId: '', startDate: '', endDate: '' })}
                >
                  Limpar
                </Button>
              </div>

              {/* Ordenação */}
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Ordenar:</span>
                <Select
                  value={sortBy}
                  onValueChange={(value) => setSortBy(value as 'date' | 'value')}
                >
                  <option value="date">Data</option>
                  <option value="value">Valor</option>
                </Select>
                
                <Select
                  value={sortOrder}
                  onValueChange={(value) => setSortOrder(value as 'asc' | 'desc')}
                >
                  <option value="desc">↓</option>
                  <option value="asc">↑</option>
                </Select>
              </div>


            </div>
          </div>
        </div>

        {/* Área principal com overflow */}
        <div className="flex-1 overflow-hidden w-full min-w-0">
          <div className="h-full overflow-y-auto">
            


              {/* Kanban de Feedbacks */}
              <div className="flex-1 overflow-hidden w-full">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                    <FileText className="h-5 w-5" />
                    <span>Kanban de Feedbacks do Gestor</span>
                  </h2>
                  {feedbacksData && (
                    <Badge variant="outline">
                      {feedbacksData.pagination.total} total
                    </Badge>
                  )}
                </div>
                <div className="h-full overflow-x-auto w-full">
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-gray-600 dark:text-gray-400">Carregando...</p>
              </div>
            ) : feedbacksData?.feedbacks?.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">Nenhum feedback encontrado</p>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={() => {
                  // Por enquanto, não implementamos drag and drop entre colunas
                  // pois isso mudaria o status do feedback
                }}
              >
                <div className="flex space-x-4 min-w-max px-4">
                  {/* Coluna: Pendente */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-0 w-[380px] flex-shrink-0">
                    <div className="flex items-center justify-between mb-4 p-4 pb-0">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                        <Clock className="h-5 w-5 text-yellow-500" />
                        <span>Pendente</span>
                      </h3>
                      <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                        {feedbacksData?.feedbacks?.filter((f: Feedback) => !f.acaoGestorConcluida && !f.acaoGestorNegada).length}
                      </Badge>
                    </div>
                    <SortableContext
                      items={getSortedFeedbacks(getFilteredFeedbacks(feedbacksData?.feedbacks || []).filter((f: Feedback) => !f.acaoGestorConcluida && !f.acaoGestorNegada)).map((f: Feedback) => f.id) || []}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-3 px-4">
                        {getSortedFeedbacks(getFilteredFeedbacks(feedbacksData?.feedbacks || []).filter((f: Feedback) => !f.acaoGestorConcluida && !f.acaoGestorNegada)).map((feedback: Feedback) => (
                          <SortableFeedbackCard
                            key={feedback.id}
                            feedback={feedback}
                            onOpenDetails={(fb) => {
                              setSelectedFeedback(fb);
                              setIsDetailsOpen(true);
                            }}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </div>

                  {/* Coluna: Concluída */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-0 w-[380px] flex-shrink-0">
                    <div className="flex items-center justify-between mb-4 p-4 pb-0">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <span>Concluída</span>
                      </h3>
                      <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        {feedbacksData?.feedbacks?.filter((f: Feedback) => f.acaoGestorConcluida).length}
                      </Badge>
                    </div>
                    <SortableContext
                      items={getSortedFeedbacks(getFilteredFeedbacks(feedbacksData?.feedbacks || []).filter((f: Feedback) => f.acaoGestorConcluida)).map((f: Feedback) => f.id) || []}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-3 px-4">
                        {getSortedFeedbacks(getFilteredFeedbacks(feedbacksData?.feedbacks || []).filter((f: Feedback) => f.acaoGestorConcluida)).map((feedback: Feedback) => (
                          <SortableFeedbackCard
                            key={feedback.id}
                            feedback={feedback}
                            onOpenDetails={(fb) => {
                              setSelectedFeedback(fb);
                              setIsDetailsOpen(true);
                            }}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </div>

                  {/* Coluna: Pagamento Confirmado */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-0 w-[380px] flex-shrink-0">
                    <div className="flex items-center justify-between mb-4 p-4 pb-0">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                        <DollarSign className="h-5 w-5 text-emerald-500" />
                        <span>Pagamento Confirmado</span>
                      </h3>
                      <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">
                        {feedbacksData?.feedbacks?.filter((f: Feedback) => f.pagamentoConfirmado).length}
                      </Badge>
                    </div>
                    <SortableContext
                      items={getSortedFeedbacks(getFilteredFeedbacks(feedbacksData?.feedbacks || []).filter((f: Feedback) => f.pagamentoConfirmado)).map((f: Feedback) => f.id) || []}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-3 px-4">
                        {getSortedFeedbacks(getFilteredFeedbacks(feedbacksData?.feedbacks || []).filter((f: Feedback) => f.pagamentoConfirmado)).map((feedback: Feedback) => (
                          <SortableFeedbackCard
                            key={feedback.id}
                            feedback={feedback}
                            onOpenDetails={(fb) => {
                              setSelectedFeedback(fb);
                              setIsDetailsOpen(true);
                            }}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </div>

                  {/* Coluna: Negada */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-0 w-[380px] flex-shrink-0">
                    <div className="flex items-center justify-between mb-4 p-4 pb-0">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                        <AlertCircle className="h-5 w-5 text-red-500" />
                        <span>Negada</span>
                      </h3>
                      <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                        {feedbacksData?.feedbacks?.filter((f: Feedback) => f.acaoGestorNegada).length}
                      </Badge>
                    </div>
                    <SortableContext
                      items={getSortedFeedbacks(getFilteredFeedbacks(feedbacksData?.feedbacks || []).filter((f: Feedback) => f.acaoGestorNegada)).map((f: Feedback) => f.id) || []}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-3 px-4">
                        {getSortedFeedbacks(getFilteredFeedbacks(feedbacksData?.feedbacks || []).filter((f: Feedback) => f.acaoGestorNegada)).map((feedback: Feedback) => (
                          <SortableFeedbackCard
                            key={feedback.id}
                            feedback={feedback}
                            onOpenDetails={(fb) => {
                              setSelectedFeedback(fb);
                              setIsDetailsOpen(true);
                            }}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </div>
                </div>
              </DndContext>
            )}
          </div>
        </div>
      </div>

      {/* Modal para Concluir Ação */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Concluir Ação do Gestor"
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">
              Observações sobre as alterações realizadas no ERP *
            </label>
            <Textarea
              value={concluirForm.observacoesGestor}
              onChange={(e) => setConcluirForm({ ...concluirForm, observacoesGestor: e.target.value })}
              placeholder="Descreva as alterações realizadas no ERP..."
              rows={4}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">
              Alterações Realizadas (opcional)
            </label>
            <div className="space-y-2">
              {['Atualização de status', 'Alteração de valor', 'Prorrogação de vencimento', 'Outras'].map((alteracao) => (
                <label key={alteracao} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={concluirForm.alteracoesRealizadas.includes(alteracao)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setConcluirForm({
                          ...concluirForm,
                          alteracoesRealizadas: [...concluirForm.alteracoesRealizadas, alteracao],
                        });
                      } else {
                        setConcluirForm({
                          ...concluirForm,
                          alteracoesRealizadas: concluirForm.alteracoesRealizadas.filter(a => a !== alteracao),
                        });
                      }
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">{alteracao}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
              disabled={concluirAcaoMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConcluirAcao}
              disabled={concluirAcaoMutation.isPending || !concluirForm.observacoesGestor.trim()}
            >
              {concluirAcaoMutation.isPending ? 'Concluindo...' : 'Concluir Ação'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Detalhes do Feedback */}
      <Modal
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        title="Detalhes do Feedback"
        size="lg"
      >
        {selectedFeedback && (
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                <span className="font-medium">Cliente:</span> {selectedFeedback.titulo?.clienteFornecedor?.nome || selectedFeedback.clienteFornecedor?.nome}
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                <span className="font-medium">Empresa:</span> {selectedFeedback.titulo?.company?.name || selectedFeedback.clienteFornecedor?.company?.name || '—'}
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-medium">Operador:</span> {selectedFeedback.user.name}
              </p>
            </div>

            <div>
              <p className="text-sm font-medium mb-2">Descrição do Operador</p>
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                  {selectedFeedback.descricao}
                </div>
                
                {/* Informações adicionais do feedback */}
                {(selectedFeedback.tipoNegociacao || selectedFeedback.valorNegociado || selectedFeedback.proximoContato) && (
                  <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                      {selectedFeedback.tipoNegociacao && (
                        <div>
                          <span className="font-medium text-gray-600 dark:text-gray-400">Tipo:</span>
                          <span className="ml-1 text-gray-700 dark:text-gray-300">{selectedFeedback.tipoNegociacao}</span>
                        </div>
                      )}
                      {selectedFeedback.valorNegociado && (
                        <div>
                          <span className="font-medium text-gray-600 dark:text-gray-400">Valor:</span>
                          <span className="ml-1 text-green-600 dark:text-green-400 font-medium">{selectedFeedback.valorNegociado}</span>
                        </div>
                      )}
                      {selectedFeedback.proximoContato && (
                        <div>
                          <span className="font-medium text-gray-600 dark:text-gray-400">Próximo Contato:</span>
                          <span className="ml-1 text-gray-700 dark:text-gray-300">{new Date(selectedFeedback.proximoContato).toLocaleDateString('pt-BR')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-slate-400">
                    <span>Registrado em: {new Date(selectedFeedback.createdAt).toLocaleString('pt-BR')}</span>
                    <span>Operador: {selectedFeedback.user.name}</span>
                  </div>
                </div>
              </div>
            </div>

            {selectedFeedback.titulosNegociados && selectedFeedback.titulosNegociados.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Títulos Relacionados</p>
                <div className="space-y-3">
                  {selectedFeedback.titulosNegociados.map((t) => (
                    <div key={t.id} className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium">{t.numero} - {t.company?.name}</span>
                        <span className="text-green-600 dark:text-green-400 font-medium">{formatCurrency(t.valor)}</span>
                      </div>
                      
                      {/* Ações do Boleto */}
                      <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <div className="flex items-center space-x-1 mb-2">
                          <ExternalLink className="h-4 w-4 text-blue-600 dark:text-blue-300" />
                          <span className="font-medium text-blue-700 dark:text-blue-300 text-sm">Ações do Boleto</span>
                        </div>
                        
                        {(t as any).urlBoleto ? (
                          <>
                            <p className="text-blue-600 dark:text-blue-400 break-all mb-3 text-xs">
                              {(t as any).urlBoleto}
                            </p>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                onClick={() => copyToClipboard((t as any).urlBoleto)}
                                variant="outline"
                                size="sm"
                                className="h-8 px-3 text-xs"
                              >
                                <Copy className="h-3 w-3 mr-1" />
                                Copiar
                              </Button>
                              <Button
                                onClick={() => window.open((t as any).urlBoleto, '_blank')}
                                variant="outline"
                                size="sm"
                                className="h-8 px-3 text-xs"
                              >
                                <ExternalLink className="h-3 w-3 mr-1" />
                                Abrir
                              </Button>
                              <Button
                                onClick={() => openWhatsApp(
                                  (t as any).urlBoleto,
                                  selectedFeedback.titulo?.clienteFornecedor?.nome || selectedFeedback.clienteFornecedor?.nome || '',
                                  selectedFeedback.titulo?.clienteFornecedor?.telefone || selectedFeedback.clienteFornecedor?.telefone
                                )}
                                variant="outline"
                                size="sm"
                                className="h-8 px-3 text-xs text-green-600 border-green-200 hover:bg-green-50"
                              >
                                <MessageCircle className="h-3 w-3 mr-1" />
                                WhatsApp
                              </Button>
                              <Button
                                onClick={() => openEmail(
                                  (t as any).urlBoleto,
                                  selectedFeedback.titulo?.clienteFornecedor?.nome || selectedFeedback.clienteFornecedor?.nome || '',
                                  selectedFeedback.titulo?.clienteFornecedor?.email || selectedFeedback.clienteFornecedor?.email
                                )}
                                variant="outline"
                                size="sm"
                                className="h-8 px-3 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                              >
                                <Mail className="h-3 w-3 mr-1" />
                                E-mail
                              </Button>
                            </div>
                          </>
                        ) : (
                          <div className="space-y-3 px-4">
                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                              Nenhum link de boleto disponível para este título.
                            </p>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                onClick={() => openWhatsApp(
                                  `Olá! Segue o link do boleto para o título ${t.numero}: [Link do boleto]`,
                                  selectedFeedback.titulo?.clienteFornecedor?.nome || selectedFeedback.clienteFornecedor?.nome || '',
                                  selectedFeedback.titulo?.clienteFornecedor?.telefone || selectedFeedback.clienteFornecedor?.telefone
                                )}
                                variant="outline"
                                size="sm"
                                className="h-8 px-3 text-xs text-green-600 border-green-200 hover:bg-green-50"
                              >
                                <MessageCircle className="h-3 w-3 mr-1" />
                                WhatsApp
                              </Button>
                              <Button
                                onClick={() => openEmail(
                                  `Link do boleto - Título ${t.numero}`,
                                  selectedFeedback.titulo?.clienteFornecedor?.nome || selectedFeedback.clienteFornecedor?.nome || '',
                                  selectedFeedback.titulo?.clienteFornecedor?.email || selectedFeedback.clienteFornecedor?.email
                                )}
                                variant="outline"
                                size="sm"
                                className="h-8 px-3 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                              >
                                <Mail className="h-3 w-3 mr-1" />
                                E-mail
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedFeedback.observacoesGestor && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded border border-blue-200 dark:border-blue-800">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1">Observações do Gestor</p>
                <p className="text-sm text-blue-700 dark:text-blue-400">{selectedFeedback.observacoesGestor}</p>
              </div>
            )}

            {selectedFeedback.acaoGestorNegada && selectedFeedback.motivoNegacao && (
              <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded border border-red-200 dark:border-red-800">
                <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">Motivo da Negação</p>
                <p className="text-sm text-red-700 dark:text-red-400">{selectedFeedback.motivoNegacao}</p>
              </div>
            )}

            {/* Anexo de Áudio */}
            {selectedFeedback.audio && (
              <div>
                <p className="text-sm font-medium mb-2">Anexo de Áudio</p>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-800 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-blue-600 dark:text-blue-300" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100 truncate">
                        {selectedFeedback.audio.originalName}
                      </p>
                      <p className="text-xs text-blue-600 dark:text-blue-300">
                        Tamanho: {(selectedFeedback.audio.fileSize / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (selectedFeedback.audio?.url) {
                            window.open(selectedFeedback.audio.url, '_blank');
                          }
                        }}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Ouvir
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Botões de Ação */}
            {(() => {
              const isPendente = !selectedFeedback.acaoGestorConcluida && !selectedFeedback.acaoGestorNegada;
              const isConcluido = selectedFeedback.acaoGestorConcluida && !selectedFeedback.acaoGestorNegada;
              
              return (isPendente || isConcluido) && (
                <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  {/* Botões de Concluir e Negar (apenas para feedbacks pendentes) */}
                  {isPendente && (
                    <div className="flex space-x-3">
                      <Button
                        onClick={() => {
                          openConcluirModal(selectedFeedback);
                          setIsDetailsOpen(false);
                        }}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Concluir Ação
                      </Button>
                      <Button
                        onClick={() => {
                          openNegarModal(selectedFeedback);
                          setIsDetailsOpen(false);
                        }}
                        variant="outline"
                        className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <AlertCircle className="w-4 h-4 mr-2" />
                        Negar Solicitação
                      </Button>
                    </div>
                  )}

                  {/* Botão Confirmar Pagamento (para feedbacks pendentes e concluídos) */}
                  {!selectedFeedback.pagamentoConfirmado && (
                    <Button
                      onClick={() => {
                        confirmarPagamento(selectedFeedback.id);
                        setIsDetailsOpen(false);
                      }}
                      variant="outline"
                      className="w-full text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                      disabled={confirmarPagamentoMutation.isPending}
                    >
                      <DollarSign className="w-4 h-4 mr-2" />
                      {confirmarPagamentoMutation.isPending ? 'Confirmando...' : 'Confirmar Pagamento'}
                    </Button>
                  )}
                </div>
              );
            })()}

          </div>
        )}
      </Modal>
      {/* Modal para Negar Solicitação */}
      <Modal
        isOpen={isNegarModalOpen}
        onClose={() => setIsNegarModalOpen(false)}
        title="Negar Solicitação"
        size="lg"
      >
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <h4 className="text-sm font-medium text-red-800">
                Atenção: Esta ação não pode ser desfeita
              </h4>
            </div>
            <p className="text-sm text-red-700 mt-2">
              Ao negar esta solicitação, o operador será notificado e a ação será marcada como negada.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">
              Motivo da Negação *
            </label>
            <Textarea
              value={negarForm.motivoNegacao}
              onChange={(e) => setNegarForm({ ...negarForm, motivoNegacao: e.target.value })}
              placeholder="Explique o motivo da negação da solicitação..."
              rows={4}
              className="border-red-200 focus:border-red-500"
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setIsNegarModalOpen(false)}
              disabled={negarSolicitacaoMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleNegarSolicitacao}
              disabled={negarSolicitacaoMutation.isPending || !negarForm.motivoNegacao.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {negarSolicitacaoMutation.isPending ? 'Negando...' : 'Negar Solicitação'}
            </Button>
          </div>
        </div>
      </Modal>
      </div>
      </div>
    </>
  );
};
